import {
  resetSessionRecordCursorCommand,
  syncHardwareConfigCommands,
} from "./commands";
import {
  buildImportedScans,
  createDownloadState,
  DownloadMachineResultType,
  reduceDownloadFrame,
} from "./savedScansStateMachine";
import { normaliseEid, tryParseEidOutputFormat } from "./eid";
import {
  isRetryableDeviceErrorCode,
  matchesResponseMode,
  parseXrs2iFrame,
} from "./frames";
import {
  CommandResponseMode,
  DownloadState,
  type EIDOutputFormat,
  FrameType,
  type IRxTransport,
  type ITxTransport,
  type DeviceCommand,
  DeviceMode,
  type Xrs2iEvent,
  type Xrs2iDeviceEventHandler,
  Xrs2iEventType,
  type Xrs2iFrame,
  type Xrs2iDeviceHandle,
} from "./types";

const CANCEL_IGNORE_WINDOW_MS = 500;
const CANCEL_LIVE_SCAN_GUARD_WINDOW_MS = 3000;
const TIMEOUT_ERROR_CODE = "TIMEOUT";
const BUSY_RETRY_BASE_DELAY_MS = 250;
const BUSY_RETRY_MAX_DELAY_MS = 2000;

interface PendingCommand extends DeviceCommand {
  retryCount: number;
}

interface DeviceState {
  // Track the current scanner mode so the protocol can tell live scan handling from saved-session import handling.
  mode: DeviceMode;
  // Store the scanner's EID output format after the device confirms it so EID parsing can match the payload style.
  eidOutputFormat?: EIDOutputFormat;
  // Hold the saved-session import state while the scanner walks session IDs and records.
  downloadState?: DownloadState;
  // Queue commands when the scanner expects a specific ACK/data sequence and we cannot send the next command yet.
  commandQueue: DeviceCommand[];
  // Remember the command that is currently waiting for a matching response from the device.
  pendingCommand?: PendingCommand;
  // Retry a command when no response arrives before the command timeout expires.
  commandTimeout?: ReturnType<typeof setTimeout>;
  // Delay retries after busy responses so the scanner has time to clear the operation that blocked us.
  commandRetryDelayTimeout?: ReturnType<typeof setTimeout>;
  // Ignore late frames for a short period after cancellation so a cancelled download does not restart itself.
  ignoreIncomingUntil: number;
  // Block live-scan emission briefly after cancellation to avoid treating delayed import rows as real-time scans.
  suppressLiveScansUntil: number;
  // Prevent nested queue flushing while a command send is already in progress.
  isFlushingQueue: boolean;
}

/**
 * Creates a live XRS2i protocol device for one transport pair.
 * The returned handle owns command sequencing, retries, saved-session import, and teardown for that scanner.
 */
export function createXrs2iDevice(
  tx: ITxTransport,
  rx: IRxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): Xrs2iDeviceHandle {
  const state: DeviceState = {
    mode: DeviceMode.IDLE,
    commandQueue: [],
    ignoreIncomingUntil: 0,
    suppressLiveScansUntil: 0,
    isFlushingQueue: false,
  };

  const unsubscribe = rx.onReceive(payload => {
    receive(state, tx, payload, onEvent);
  });

  return {
    async resetHardwareConfig() {
      prepareForFormatRequest(state);
      queueCommands(state, tx, syncHardwareConfigCommands(), onEvent);
    },

    async readSavedScans() {
      resetCommandPipeline(state);
      state.mode = DeviceMode.DOWNLOADING_SAVED_SCANS;
      state.downloadState = createDownloadState();
      state.ignoreIncomingUntil = 0;
      state.suppressLiveScansUntil = 0;
      queueCommands(state, tx, [resetSessionRecordCursorCommand()], onEvent);
    },

    cancelReadSavedScans() {
      resetCommandPipeline(state);
      state.mode = DeviceMode.IDLE;
      state.downloadState = undefined;
      state.ignoreIncomingUntil = Date.now() + CANCEL_IGNORE_WINDOW_MS;
      state.suppressLiveScansUntil =
        Date.now() + CANCEL_LIVE_SCAN_GUARD_WINDOW_MS;
    },

    destroy() {
      unsubscribe();
      resetCommandPipeline(state);
      state.downloadState = undefined;
      state.mode = DeviceMode.IDLE;
      state.suppressLiveScansUntil = 0;
    },
  };
}

function prepareForFormatRequest(state: DeviceState): void {
  // Reset the queue before asking for the format so stale download work cannot race the hardware sync request.
  resetCommandPipeline(state);
  state.downloadState = undefined;
  state.mode = DeviceMode.AWAITING_EID_OUTPUT_FORMAT;
  state.ignoreIncomingUntil = 0;
  state.suppressLiveScansUntil = 0;
}

/**
 * Handles one raw payload from the scanner and routes it through the current protocol mode.
 * The order matters because ACKs, errors, saved-session frames, and live scan payloads all share the same transport.
 */
function receive(
  state: DeviceState,
  tx: ITxTransport,
  serializedPayload: string,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  // Release the input hold-off once its deadline has passed so the next real scanner frame can be processed.
  if (
    state.ignoreIncomingUntil > 0 &&
    Date.now() >= state.ignoreIncomingUntil
  ) {
    state.ignoreIncomingUntil = 0;
  }

  if (state.ignoreIncomingUntil > 0) {
    return;
  }

  if (
    state.suppressLiveScansUntil > 0 &&
    Date.now() >= state.suppressLiveScansUntil
  ) {
    state.suppressLiveScansUntil = 0;
  }

  if (state.suppressLiveScansUntil > 0) {
    return;
  }

  const frame = parseXrs2iFrame(serializedPayload);

  if (frame.type === FrameType.ERROR) {
    onErrorFrame(state, tx, frame.code, onEvent);
    return;
  }

  resolvePendingCommand(state, tx, frame, onEvent);

  if (
    state.mode === DeviceMode.DOWNLOADING_SAVED_SCANS &&
    state.downloadState
  ) {
    onDownloadFrame(state, tx, frame, onEvent);
    return;
  }

  if (frame.type === FrameType.BRACKET) {
    const detectedFormat = tryParseEidOutputFormat(frame.raw);
    if (detectedFormat !== undefined) {
      state.eidOutputFormat = detectedFormat;
      if (state.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
        state.mode = DeviceMode.IDLE;
      }
      return;
    }
  }

  if (state.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
    state.mode = DeviceMode.IDLE;
  }

  processLiveFrame(state, frame, onEvent);
}

/**
 * Applies one scanner frame to the saved-session import state machine.
 * This keeps the import sequence linear so each ACK or payload advances the next command only when the device is ready.
 */
function onDownloadFrame(
  state: DeviceState,
  tx: ITxTransport,
  frame: Xrs2iFrame,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { downloadState } = state;
  if (!downloadState) {
    finishDownload(state, false, onEvent);
    return;
  }

  const result = reduceDownloadFrame(
    downloadState,
    frame,
    state.eidOutputFormat,
  );

  for (const event of result.events) {
    emit(onEvent, event);
  }

  if (result.type === DownloadMachineResultType.COMPLETE) {
    resetCommandPipeline(state);
    finishDownload(state, result.success, onEvent);
    return;
  }

  queueCommands(state, tx, result.commands, onEvent);
}

/**
 * Handles an error frame from the scanner and decides whether the current command should retry.
 * Busy responses can be retried with backoff, while all other errors close out the active command pipeline.
 */
function onErrorFrame(
  state: DeviceState,
  tx: ITxTransport,
  code: string,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  if (
    state.pendingCommand &&
    state.pendingCommand.retryOnBusy &&
    isRetryableDeviceErrorCode(code)
  ) {
    if (retryPendingCommandWithBackoff(state, tx, onEvent)) {
      return;
    }
  }

  resetCommandPipeline(state);
  emit(onEvent, { type: Xrs2iEventType.ERROR, code });

  if (state.mode === DeviceMode.DOWNLOADING_SAVED_SCANS) {
    finishDownload(state, false, onEvent);
    return;
  }

  state.mode = DeviceMode.IDLE;
}

/**
 * Queues commands that must be sent in order and starts flushing them immediately when possible.
 * The queue exists because the scanner expects strict request/response sequencing for ACK and data commands.
 */
function queueCommands(
  state: DeviceState,
  tx: ITxTransport,
  commands: DeviceCommand[],
  onEvent: Xrs2iDeviceEventHandler,
): void {
  if (commands.length === 0) {
    return;
  }

  state.commandQueue.push(...commands);
  flushCommandQueue(state, tx, onEvent);
}

/**
 * Sends queued commands until one requires a response or the queue runs empty.
 * Commands that do not expect a reply are sent immediately so the protocol can advance without waiting on the device.
 */
function flushCommandQueue(
  state: DeviceState,
  tx: ITxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  if (state.isFlushingQueue || state.pendingCommand) {
    return;
  }

  state.isFlushingQueue = true;

  try {
    while (!state.pendingCommand && state.commandQueue.length > 0) {
      const nextCommand = state.commandQueue.shift();
      if (!nextCommand) {
        return;
      }

      if (nextCommand.responseMode === CommandResponseMode.NONE) {
        void tx.send(nextCommand.payload);
        continue;
      }

      state.pendingCommand = {
        ...nextCommand,
        retryCount: 0,
      };
      schedulePendingCommandTimeout(state, tx, onEvent);
      void tx.send(nextCommand.payload);
      return;
    }
  } finally {
    state.isFlushingQueue = false;
  }
}

/**
 * Clears the current command when the incoming frame matches the response mode we were waiting for.
 * This ensures the next queued command only runs after the scanner has acknowledged the previous one.
 */
function resolvePendingCommand(
  state: DeviceState,
  tx: ITxTransport,
  frame: Xrs2iFrame,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  if (!matchesResponseMode(pendingCommand.responseMode, frame)) {
    return;
  }

  clearPendingCommand(state);
  flushCommandQueue(state, tx, onEvent);
}

/**
 * Starts or refreshes the timeout for the current in-flight command.
 * If the scanner goes silent, the timeout is what drives retry or failure cleanup.
 */
function schedulePendingCommandTimeout(
  state: DeviceState,
  tx: ITxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  clearCommandTimeout(state);
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  state.commandTimeout = setTimeout(() => {
    state.commandTimeout = undefined;
    onPendingCommandTimeout(state, tx, onEvent);
  }, pendingCommand.timeoutMs);
}

/**
 * Handles a command timeout after the scanner failed to answer in time.
 * The retry limit protects against endless resend loops when the device is disconnected or stuck.
 */
function onPendingCommandTimeout(
  state: DeviceState,
  tx: ITxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  if (pendingCommand.retryCount < pendingCommand.maxRetries) {
    retryPendingCommand(state, tx, onEvent);
    return;
  }

  resetCommandPipeline(state);
  emit(onEvent, { type: Xrs2iEventType.ERROR, code: TIMEOUT_ERROR_CODE });

  if (state.mode === DeviceMode.DOWNLOADING_SAVED_SCANS) {
    finishDownload(state, false, onEvent);
    return;
  }

  state.mode = DeviceMode.IDLE;
}

/**
 * Resends the current command after a normal timeout retry.
 * This keeps simple missed-response cases resilient without forcing the user to restart the session.
 */
function retryPendingCommand(
  state: DeviceState,
  tx: ITxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  pendingCommand.retryCount += 1;
  clearCommandTimeout(state);
  schedulePendingCommandTimeout(state, tx, onEvent);
  void tx.send(pendingCommand.payload);
}

/**
 * Retries a busy command after a short exponential backoff.
 * Busy errors usually mean the scanner is still processing, so delaying the resend gives it time to recover.
 */
function retryPendingCommandWithBackoff(
  state: DeviceState,
  tx: ITxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): boolean {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return false;
  }

  if (pendingCommand.retryCount >= pendingCommand.maxRetries) {
    return false;
  }

  const backoffMs = computeBusyRetryBackoffMs(pendingCommand.retryCount);
  pendingCommand.retryCount += 1;
  clearCommandTimeout(state);
  clearCommandRetryDelayTimeout(state);

  state.commandRetryDelayTimeout = setTimeout(() => {
    state.commandRetryDelayTimeout = undefined;

    if (state.pendingCommand !== pendingCommand) {
      return;
    }

    schedulePendingCommandTimeout(state, tx, onEvent);
    void tx.send(pendingCommand.payload);
  }, backoffMs);

  return true;
}

function resetCommandPipeline(state: DeviceState): void {
  // Clear both timers before dropping queued commands so a stale retry cannot fire after the pipeline has moved on.
  clearCommandTimeout(state);
  clearCommandRetryDelayTimeout(state);
  state.commandQueue.length = 0;
  state.pendingCommand = undefined;
}

function clearPendingCommand(state: DeviceState): void {
  clearCommandTimeout(state);
  state.pendingCommand = undefined;
}

function clearCommandTimeout(state: DeviceState): void {
  if (state.commandTimeout) {
    clearTimeout(state.commandTimeout);
    state.commandTimeout = undefined;
  }
}

function clearCommandRetryDelayTimeout(state: DeviceState): void {
  if (state.commandRetryDelayTimeout) {
    clearTimeout(state.commandRetryDelayTimeout);
    state.commandRetryDelayTimeout = undefined;
  }
}

function computeBusyRetryBackoffMs(retryCount: number): number {
  return Math.min(
    BUSY_RETRY_BASE_DELAY_MS * 2 ** retryCount,
    BUSY_RETRY_MAX_DELAY_MS,
  );
}

/**
 * Finishes the saved-session import and emits the final progress summary.
 * Successful runs include the accumulated session scans, while failed runs only report counts and completion state.
 */
function finishDownload(
  state: DeviceState,
  success: boolean,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { downloadState } = state;
  const completedSessions = downloadState?.completedSessionCount ?? 0;
  const totalSessions = downloadState?.totalSessionCount ?? null;
  const importedScans =
    success && downloadState ? buildImportedScans(downloadState) : {};

  // Reset the import state before emitting completion so consumers see the device as idle during the callback.
  state.downloadState = undefined;
  state.mode = DeviceMode.IDLE;

  emit(onEvent, {
    type: Xrs2iEventType.SAVED_SCANS_COMPLETE,
    success,
    completedSessions,
    totalSessions,
    importedScans,
  });
}

/**
 * Interprets live scan payloads once the scanner is no longer in saved-session import mode.
 * Valid EIDs become scan events, while unexpected non-empty payloads become invalid-EID notifications.
 */
function processLiveFrame(
  state: DeviceState,
  frame: Xrs2iFrame,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  if (frame.type !== FrameType.TEXT) {
    return;
  }

  const eid = normaliseEid(frame.value, state.eidOutputFormat);
  if (eid) {
    emit(onEvent, {
      type: Xrs2iEventType.SCAN,
      eid,
      created: new Date().toISOString(),
    });
    return;
  }

  if (!frame.value) {
    return;
  }

  emit(onEvent, {
    type: Xrs2iEventType.INVALID_EID,
  });
}

function emit(onEvent: Xrs2iDeviceEventHandler, event: Xrs2iEvent): void {
  try {
    onEvent(event);
  } catch (error) {
    // Isolate consumer failures so protocol cleanup can finish even if the event handler throws.
    // eslint-disable-next-line no-console
    console.error("Unhandled XRS2i event handler error", error);
  }
}
